import React from 'react';
import Chart from 'react-apexcharts';

type Data = { x: string | number; y: number };

type Props = {
  data: Data[];
  title: string;
  dataSeriesName: string;
  style?: React.CSSProperties;
  xaxis?: Record<string, unknown>;
  yaxisFormatter?: (value: string | number | undefined) => string;
  tooltipDateFormat?: string;
  toolbar?: boolean;
  additionalLines?: { name: string; data: Data[] }[];
  cleaningCondition?: (arg0: Data) => boolean;
  hideDate?: boolean;
  /** explicit chart height (px or %). When omitted, ApexCharts uses its own
   *  default — existing callers are unaffected. */
  height?: number | string;
  /** ApexCharts tooltip options passthrough (e.g. a `custom` renderer). Merged
   *  over the defaults; omitted callers are unaffected. */
  tooltip?: Record<string, unknown>;
  /** fixed y-axis bounds (data units). When omitted, ApexCharts auto-scales. */
  yMin?: number;
  yMax?: number;
};

const AreaChart: React.FC<Props> = ({
  data,
  title,
  dataSeriesName,
  style,
  yaxisFormatter,
  xaxis,
  tooltipDateFormat,
  toolbar,
  additionalLines,
  cleaningCondition,
  hideDate,
  height,
  tooltip,
  yMin,
  yMax,
}) => {
  if (cleaningCondition && data.some(cleaningCondition)) data = [];

  const options: Record<string, unknown> = {
    chart: {
      toolbar: {
        show: toolbar || false,
      },
      animations: {
        enabled: false,
      },
    },
    title: {
      text: title,
    },
    stroke: { curve: 'smooth' },
    dataLabels: {
      enabled: false,
    },
    fill: {
      type: 'gradient',
      gradient: {
        shadeIntensity: 1,
        inverseColors: false,
        opacityFrom: 0.5,
        opacityTo: 0,
        stops: [0, 90, 100],
      },
    },
    yaxis: {
      ...(yMin != null ? { min: yMin } : {}),
      ...(yMax != null ? { max: yMax } : {}),
      labels: {
        formatter: yaxisFormatter
          ? yaxisFormatter
          : (value: string | number | undefined) =>
              value?.toLocaleString?.() ?? '',
      },
    },
    xaxis: xaxis ?? {
      type: 'datetime',
      tooltip: {
        enabled: false,
      },
      tickPlacement: 'between',
      labels: {
        show: !hideDate,
      },
    },
  };

  if (tooltipDateFormat) {
    options['tooltip'] = {
      x: {
        show: !hideDate,
        format: tooltipDateFormat,
      },
    };
  }

  if (tooltip) {
    options['tooltip'] = {
      ...(options['tooltip'] as Record<string, unknown> | undefined),
      ...tooltip,
    };
  }

  return (
    <div style={style}>
      <Chart
        options={options}
        series={[
          {
            name: dataSeriesName,
            data: data,
          },
          ...(additionalLines || []),
        ]}
        type="area"
        {...(height != null ? { height } : {})}
      />
    </div>
  );
};

export default AreaChart;
